import { AnimatePresence, m } from 'framer-motion';
import { create } from 'zustand';
import { CheckCircle2, XCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export type ToastTone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastState {
  items: ToastItem[];
  push: (message: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (message, tone = 'success') => {
    const id = nextId++;
    set((s) => ({ items: [...s.items, { id, message, tone }] }));
    // §8.6 — Toast 2s turadi
    window.setTimeout(() => {
      set((s) => ({ items: s.items.filter((x) => x.id !== id) }));
    }, 2000);
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

/** Har qanday joydan chaqirish uchun: `showToast(t('Saqlandi'))` */
export function showToast(message: string, tone: ToastTone = 'success'): void {
  useToastStore.getState().push(message, tone);
  if ('vibrate' in navigator) navigator.vibrate(20);
}

const TONE_ICON: Record<ToastTone, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
};
const TONE_CLASS: Record<ToastTone, string> = {
  success: 'text-ok',
  error: 'text-danger',
  info: 'text-info',
};

/** App.tsx ildizida bitta marta joylashtiriladi */
export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[200] flex flex-col items-center gap-2 p-4 mt-2 safe-top">
      <AnimatePresence>
        {items.map((item) => {
          const Icon = TONE_ICON[item.tone];
          return (
            <m.div
              key={item.id}
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="pointer-events-auto flex max-w-[420px] items-center gap-2 rounded-pill bg-inverse px-5 py-3 text-white shadow-float"
            >
              <Icon size={18} strokeWidth={1.75} className={cn(TONE_CLASS[item.tone])} />
              <span className="text-[14px] font-medium">{item.message}</span>
            </m.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
